import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  CardActions,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Send as SendIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Smartphone as SmartphoneIcon,
  Laptop as LaptopIcon,
  Tablet as TabletIcon,
  DesktopWindows as DesktopIcon,
  Tv as TvIcon,
  DevicesOther as DevicesIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  height: '100%'
}));

const ResponseCard = styled(Card)(({ theme, isSolution }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  border: isSolution ? `2px solid ${theme.palette.success.main}` : 'none',
  position: 'relative',
  overflow: 'visible',
  '&::before': isSolution ? {
    content: '"Solution"',
    position: 'absolute',
    top: -12,
    left: 16,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    zIndex: 1
  } : {}
}));

const ViewIssue = () => {
  const { issueId } = useParams();
  const navigate = useNavigate();
  
  const [issue, setIssue] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseLoading, setResponseLoading] = useState(false);
  const [newResponse, setNewResponse] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get icon based on category name
  const getCategoryIcon = (categoryName) => {
    switch (categoryName?.toLowerCase()) {
      case 'smartphone':
        return <SmartphoneIcon />;
      case 'laptop':
        return <LaptopIcon />;
      case 'tablet':
        return <TabletIcon />;
      case 'desktop':
        return <DesktopIcon />;
      case 'tv':
        return <TvIcon />;
      default:
        return <DevicesIcon />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Fetch issue data
  useEffect(() => {
    const fetchIssueData = async () => {
      setLoading(true);
      try {
        // Fetch issue details
        const { data: issueData, error: issueError } = await supabase
          .from('user_issues')
          .select(`
            *,
            device_categories:device_category_id(id, name),
            device_brands:device_brand_id(id, name)
          `)
          .eq('id', issueId)
          .single();
        
        if (issueError) throw issueError;
        setIssue(issueData);

        // Update view count
        await supabase
          .from('user_issues')
          .update({ views: (issueData.views || 0) + 1 })
          .eq('id', issueId);

        // Fetch responses
        const { data: responsesData, error: responsesError } = await supabase
          .from('issue_responses')
          .select('*')
          .eq('issue_id', issueId)
          .order('created_at', { ascending: true });
        
        if (responsesError) throw responsesError;
        setResponses(responsesData);

      } catch (error) {
        console.error('Error fetching issue data:', error);
        setSnackbar({
          open: true,
          message: 'Error loading issue data. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    if (issueId) {
      fetchIssueData();
    }
  }, [issueId]);

  // Submit response
  const handleSubmitResponse = async () => {
    if (!newResponse.trim()) return;
    
    setResponseLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      // Insert response
      const { data: responseData, error: responseError } = await supabase
        .from('issue_responses')
        .insert([
          {
            issue_id: parseInt(issueId),
            user_id: user?.id || null,
            content: newResponse,
            is_solution: false
          }
        ])
        .select();
      
      if (responseError) throw responseError;

      // Refresh responses
      const { data: updatedResponses, error: fetchError } = await supabase
        .from('issue_responses')
        .select('*')
        .eq('issue_id', issueId)
        .order('created_at', { ascending: true });
      
      if (fetchError) throw fetchError;
      setResponses(updatedResponses);
      
      // Clear form
      setNewResponse('');
      
      setSnackbar({
        open: true,
        message: 'Response submitted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error submitting response:', error);
      setSnackbar({
        open: true,
        message: 'Error submitting response. Please try again.',
        severity: 'error'
      });
    } finally {
      setResponseLoading(false);
    }
  };

  // Mark response as solution
  const handleMarkAsSolution = async (responseId) => {
    try {
      // Update response
      await supabase
        .from('issue_responses')
        .update({ is_solution: true })
        .eq('id', responseId);
      
      // Update issue status
      await supabase
        .from('user_issues')
        .update({ status: 'solved' })
        .eq('id', issueId);
      
      // Refresh data
      const { data: updatedIssue } = await supabase
        .from('user_issues')
        .select(`
          *,
          device_categories:device_category_id(id, name),
          device_brands:device_brand_id(id, name)
        `)
        .eq('id', issueId)
        .single();
      
      setIssue(updatedIssue);
      
      const { data: updatedResponses } = await supabase
        .from('issue_responses')
        .select('*')
        .eq('issue_id', issueId)
        .order('created_at', { ascending: true });
      
      setResponses(updatedResponses);
      
      setSnackbar({
        open: true,
        message: 'Response marked as solution!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error marking solution:', error);
      setSnackbar({
        open: true,
        message: 'Error updating solution status. Please try again.',
        severity: 'error'
      });
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!issue) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Issue not found. It may have been deleted or you don't have permission to view it.
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/issues')}
          sx={{ mt: 2 }}
        >
          Back to Issues
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/issues" underline="hover" color="inherit">
          Issues
        </MuiLink>
        <Typography color="text.primary">Issue Details</Typography>
      </Breadcrumbs>
      
      <Grid container spacing={3}>
        {/* Issue Details */}
        <Grid item xs={12}>
          <StyledPaper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                  {issue.title}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  <Chip 
                    icon={getCategoryIcon(issue.device_categories?.name)} 
                    label={issue.device_categories?.name} 
                  />
                  <Chip label={issue.device_brands?.name} variant="outlined" />
                  <Chip 
                    label={issue.status} 
                    color={
                      issue.status === 'solved' ? 'success' : 
                      issue.status === 'in progress' ? 'warning' : 
                      'default'
                    }
                  />
                  <Chip 
                    label={`Priority: ${issue.priority}`} 
                    color={
                      issue.priority === 'high' ? 'error' : 
                      issue.priority === 'medium' ? 'warning' : 
                      'default'
                    }
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 3 }}>
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="body2">
                    Posted on {formatDate(issue.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/issues')}
              >
                Back to Issues
              </Button>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line' }}>
              {issue.description}
            </Typography>
          </StyledPaper>
        </Grid>
        
        {/* Responses */}
        <Grid item xs={12}>
          <StyledPaper>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Responses ({responses.length})
            </Typography>
            
            {responses.length === 0 ? (
              <Alert severity="info" sx={{ mb: 3 }}>
                No responses yet. Be the first to respond!
              </Alert>
            ) : (
              <Box sx={{ mb: 4 }}>
                {responses.map((response) => (
                  <ResponseCard key={response.id} isSolution={response.is_solution}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar>{response.user_id ? response.user_id.substring(0, 2) : 'U'}</Avatar>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {response.user_id ? `User ${response.user_id.substring(0, 8)}` : 'Anonymous'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(response.created_at)}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body1" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                        {response.content}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                      {!response.is_solution && issue.status !== 'solved' && (
                        <Button
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleMarkAsSolution(response.id)}
                        >
                          Mark as Solution
                        </Button>
                      )}
                      <IconButton size="small" aria-label="upvote">
                        <ThumbUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" aria-label="downvote">
                        <ThumbDownIcon fontSize="small" />
                      </IconButton>
                    </CardActions>
                  </ResponseCard>
                ))}
              </Box>
            )}
            
            {/* Add Response Form */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Add Your Response
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Share your solution or ask for more information..."
                value={newResponse}
                onChange={(e) => setNewResponse(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Button
                variant="contained"
                color="primary"
                endIcon={<SendIcon />}
                onClick={handleSubmitResponse}
                disabled={responseLoading || !newResponse.trim()}
              >
                {responseLoading ? <CircularProgress size={24} /> : 'Submit Response'}
              </Button>
            </Box>
          </StyledPaper>
        </Grid>
      </Grid>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewIssue; 